import { createSimpleMutator } from '@superbet-group/web.lib.vue-utilities';
import { ActionContext } from 'vuex';

import { IState } from '@app/store';
import { inboxService } from '@core/services/inbox/InboxService';

import {
    InboxState,
    MessageRequestSuccess,
    MessagesRequest,
} from './types';
import { RouteName } from '@src/app/router/constants/RouteName';

const SET_NEW_MESSAGE_NUMBER = 'SET_NEW_MESSAGE_NUMBER';
const SET_MESSAGES = 'SET_MESSAGES';
const SET_MESSAGES_STATUS = 'SET_MESSAGES_STATUS';
const CHECK_INBOX_INTERVAL_MS = 2 * 60 * 1000; // Interval of fetching number of new messages 2 minutes
let inboxCheckInterval: NodeJS.Timeout | null = null;

function defaultState(): InboxState {
    return {
        numberOfNewMessages: 0,
        messages: [],
        messageResponseStatus: MessagesRequest.Pending,
    };
}

const store = {
    namespaced: true,
    state: defaultState(),
    mutations: {
        [SET_NEW_MESSAGE_NUMBER]: createSimpleMutator<InboxState, 'numberOfNewMessages'>('numberOfNewMessages'),
        [SET_MESSAGES]: createSimpleMutator<InboxState, 'messages'>('messages'),
        [SET_MESSAGES_STATUS]: createSimpleMutator<InboxState, 'messageResponseStatus'>('messageResponseStatus'),
    },
    actions: {
        async initUserInbox(context: ActionContext<InboxState, IState>, payload: { userId: number | string; isLogin: boolean }) {
            await inboxService.loadXtremePush();
            await context.dispatch('setUserId', payload.userId);

            context.dispatch('checkInbox');
            inboxCheckInterval = setInterval(() => {
                context.dispatch('checkInbox');
            }, CHECK_INBOX_INTERVAL_MS);

            // if user is on Notification page when logins we need to fetch all messages
            const currentRoute = context.rootGetters['navigation/route'];
            if (currentRoute.name === RouteName.BonusAndComms.Notifications && context.state.messages.length === 0) {
                context.dispatch('fetchMessages');
            }
            // show push notification after XP is loaded, but only on login action
            if (payload.isLogin) {
                context.dispatch('ui/user/messages/showPushNotification', null, { root: true });
            }
        },
        async setUserId(context: ActionContext<InboxState, IState>, userId: number | string) {
            await inboxService.setInboxUserId(userId);
        },
        async checkInbox(context: ActionContext<InboxState, IState>) {
            const numberOfNewMessages = await inboxService.fetchNumberOfNewMessages();
            context.commit(SET_NEW_MESSAGE_NUMBER, numberOfNewMessages);
        },
        updateNewMessageNumber(context: ActionContext<InboxState, IState>, newMessages: number) {
            context.commit(SET_NEW_MESSAGE_NUMBER, newMessages);
        },
        async fetchMessages(context: ActionContext<InboxState, IState>) {
            context.commit(SET_MESSAGES_STATUS, MessagesRequest.Pending);
            const inboxResponse = await inboxService.fetchMessages();
            if (inboxResponse.state === MessagesRequest.Success) {
                context.commit(SET_MESSAGES, (inboxResponse as MessageRequestSuccess).response);
            }
            context.commit(SET_MESSAGES_STATUS, inboxResponse.state);
        },
        clearInbox(context: ActionContext<InboxState, IState>) {
            context.commit(SET_MESSAGES, []);
            context.commit(SET_NEW_MESSAGE_NUMBER, 0);
            context.commit(SET_MESSAGES_STATUS, MessagesRequest.Pending);
            if (inboxCheckInterval) {
                clearInterval(inboxCheckInterval);
            }
        },
    },
};

export type InboxStore = typeof store;

export default store;
